import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import TitleRow, { TitleAction } from "../../../../shared/components/TitleRow";
import CancelModal from "../../../../shared/components/CancelModal";
import { WarningProductModal } from "../../../../configs/warningProductModal";
import { DeleteProductModal, DetailProductTitleBar } from "../../../../configs/products";
import { Product } from "../../../../data/entities";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { ROLE } from "../../../../data/enums";

interface TitleProps {
    title: string;
    data?: Product;
    setValidated: (validate: boolean) => void;
    onSubmit: Function;
    onDataChange?: () => void;
    hasUnsavedChanges?: boolean;
    onCancelClick?: () => void;
    isLoading?: boolean;
    deleteProduct?: () => Promise<void>;
    isDeleting?: boolean;
}

const Title = ({
                   title,
                   setValidated,
                   data,
                   onSubmit,
                   onDataChange,
                   hasUnsavedChanges,
                   onCancelClick,
                   isLoading,
                   deleteProduct,
                   isDeleting,
               }: TitleProps) => {
    const navigate = useNavigate();
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

    const handleCancelClick = () => {
        if (onDataChange) {
            onDataChange();
        }

        if (onCancelClick && hasUnsavedChanges) {
            onCancelClick();
        } else {
            setIsOpenCancelModal(true);
        }
    };

    const baseTitleActions: TitleAction[] = [
        {
            title: DetailProductTitleBar.cancelButton,
            onClick: handleCancelClick,
        },
        {
            title: DetailProductTitleBar.duplicateButton,
            variant: "outlined",
            onClick: () => {
                navigate(`/products/create/`, {
                    state: { duplicateData: data },
                });
            },
        },
        {
            title: DetailProductTitleBar.addButton,
            variant: "contained",
            onClick: () => {
                setValidated(true);
                onSubmit();
            },
            isLoading: isLoading,
        },
    ];
    const titleActions = deleteProduct && role === ROLE.MANAGER
        ? [
            {
                title: DetailProductTitleBar.deleteButton,
                variant: "outlined" as const,
                onClick: () => setIsOpenDeleteModal(true),
                isLoading: isDeleting,
            },
            ...baseTitleActions,
        ]
        : baseTitleActions;

    const getTitleActions = () => {
        switch (role) {
            case ROLE.STORAGE_KEEPER:
            case ROLE.FIRST_STORAGE_KEEPER:
            case ROLE.SECOND_STORAGE_KEEPER:
            case ROLE.DELIVERER:
                return [];
            case ROLE.LOGISTIC_MANAGER:
            case ROLE.LOGISTIC_STAFF:
            case ROLE.PURCHASING_STAFF:
            case ROLE.PRODUCTION_STAFF:
            case ROLE.FACTORY_STAFF:
                return titleActions.filter(
                    (item) =>
                        item.title !== DetailProductTitleBar.duplicateButton,
                );
            case ROLE.SALE_MANAGER:
            case ROLE.SALE_STAFF:
                return titleActions.filter(
                    (item) =>
                        item.title !== DetailProductTitleBar.duplicateButton,
                );
            case ROLE.MANAGER:
            case ROLE.ACCOUNTANT_MANAGER:
            case ROLE.FACTORY_MANAGER_ONE:
            case ROLE.FACTORY_MANAGER_TWO:
            case ROLE.DEPUTY_FACTORY_MANAGER_ONE:
            case ROLE.DEPUTY_FACTORY_MANAGER_TWO:
                return titleActions;
        }
    };

    return (
        <>
            <TitleRow title={title} actions={getTitleActions()} />
            {isOpenCancelModal && (
                <CancelModal
                    open={isOpenCancelModal}
                    title={WarningProductModal.title}
                    contentText={WarningProductModal.context}
                    handleConfirm={() => navigate("/products/")}
                    handleClose={() => setIsOpenCancelModal(false)}
                />
            )}
            {isOpenDeleteModal && deleteProduct && (
                <CancelModal
                    open={isOpenDeleteModal}
                    title={DeleteProductModal.title}
                    contentText={DeleteProductModal.context}
                    handleConfirm={async () => {
                        await deleteProduct();
                        setIsOpenDeleteModal(false);
                    }}
                    handleClose={() => setIsOpenDeleteModal(false)}
                    confirmButtonText={isDeleting ? "Đang xoá..." : "Xoá"}
                />
            )}
        </>
    );
};

export default Title;
